import { useEffect, useRef } from "react";

/**
 * Keeps a <textarea> exactly as tall as its content, so a long answer is never hidden
 * behind a scrollbar or pushed sideways out of view.
 *
 * Height is measured rather than guessed, so it stays correct at any font size, any
 * container width, and on any screen — a sentence that fits one line on desktop and
 * wraps to three on a phone grows to three on the phone.
 *
 * @param {string}  value              current textarea value; height recalculates on change
 * @param {object}  options
 * @param {object}  options.ref        optional existing ref to reuse (e.g. for focus)
 * @param {number}  options.minHeight  smallest height in px, so an empty box still invites writing
 * @returns {object} ref to attach to the textarea
 */
export default function useAutoGrowTextarea(value, { ref: externalRef, minHeight = 96 } = {}) {
  const internalRef = useRef(null);
  const ref = externalRef ?? internalRef;

  useEffect(() => {
    const el = ref.current;
    if (!el || el.tagName !== "TEXTAREA") return;

    // Collapse first, then measure: scrollHeight only shrinks back if the element is
    // allowed to, otherwise deleting text would leave the box stuck at its tallest.
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
  }, [value, minHeight, ref]);

  // Re-measure when the viewport changes: rotating a phone or resizing a window
  // rewraps the text, which changes how many lines it needs.
  useEffect(() => {
    const el = ref.current;
    if (!el || el.tagName !== "TEXTAREA" || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      el.style.height = "auto";
      el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [minHeight, ref]);

  return ref;
}
